// src/pages/SettingsTelegram.jsx
import { useEffect, useMemo, useRef, useState } from "react";
import api from "../auth/api";

/**
 * Pantalla para administrar MÚLTIPLES bots/cuentas de Telegram,
 * siguiendo el mismo estilo visual y mentalidad de SettingsMulti (WhatsApp).
 *
 * Usa:
 *  - GET  /telegram/accounts
 *  - GET  /telegram/accounts/:id/state
 *  - POST /telegram/accounts/:id/start
 *  - POST /telegram/accounts/:id/reset
 *
 * Opcional (si conectas Telegram a la misma arquitectura de canales/bots):
 *  - GET  /channels/:channelId/connections
 *  - GET  /channels/:channelId/connections/:connId/bot
 *  - POST /channels/:channelId/connections/:connId/bot
 *  - GET  /bots
 */

export default function SettingsTelegram() {
  const [accounts, setAccounts] = useState([]); // [{id,name,username,created_at,...}]
  const [connectedMap, setConnectedMap] = useState({}); // { [id]: boolean }
  const [stateRawMap, setStateRawMap] = useState({}); // { [id]: any }
  const [form, setForm] = useState({
    name: "",
    username: "",
    token: "",
  });
  const [savingAccount, setSavingAccount] = useState(false);
  const [error, setError] = useState("");

  // Opcional: conexiones de canal y bots (misma tabla de conexiones que WA)
  const [connectionsMap, setConnectionsMap] = useState({}); // { [channel_id]: [connections...] }
  const [bots, setBots] = useState([]); // [{id,name,...}]
  const [connBotMap, setConnBotMap] = useState({}); // { `${channelId}:${connId}`: {bot_id,is_active} }

  const pollRef = useRef({ status: null });

  const ep = useMemo(() => {
    const noCache = {
      headers: {
        "Cache-Control": "no-store",
        Pragma: "no-cache",
        "If-Modified-Since": "0",
      },
    };
    const bust = () => `?_=${Date.now()}`;

    const req = {
      get: (url) => api.get(`${url}${bust()}`, noCache),
      post: (url, body) => api.post(url + bust(), body, noCache),
    };

      // Detectar si una estructura de estado de Telegram está “conectada”
    const normalizeConnected = (state) => {
      if (!state || typeof state !== "object") return false;

      // bools directos
      for (const k of ["connected", "isConnected", "online", "is_running", "running"]) {
        if (typeof state[k] === "boolean") return !!state[k];
      }

      // strings tipo "open", "connected", "ready"
      for (const k of ["status", "state", "connection"]) {
        const v = state[k];
        if (typeof v === "string") {
          const t = v.toLowerCase();
          if (
            [
              "ok",
              "open",
              "opened",
              "connected",
              "online",
              "ready",
              "running",
              "started",
            ].includes(t)
          )
            return true;
          if (
            [
              "false",
              "close",
              "closed",
              "disconnected",
              "offline",
              "stopped",
              "error",
            ].includes(t)
          )
            return false;
        }
      }

      // Telegrames típicos: state = { running:true } o { connected:true }
      if (state.state && typeof state.state === "object") {
        const inner = state.state;
        if (typeof inner.connected === "boolean") return !!inner.connected;
        if (typeof inner.running === "boolean") return !!inner.running;
      }

      // fallback
      return false;
    };

    return {
      // Lista cuentas TG
      listAccounts: async () => {
        const { data } = await req.get("/telegram/accounts");
        return data?.accounts || [];
      },

      // Estado individual
      getState: async (id) => {
        const { data } = await req.get(`/telegram/accounts/${id}/state`);
        // data = { ok:true, state: {...} }
        return data?.state ?? data;
      },

      // Snapshot de todos los estados (si quieres ahorrar llamadas)
      getStatesSnapshot: async () => {
        const { data } = await req.get("/telegram/accounts/states");
        // tú decides el formato del snapshot. Soportamos:
        //  A) { ok:true, states: [{id,state},...] }
        //  B) { ok:true, states: { "1":{...}, "2":{...} } }
        const out = {};
        if (Array.isArray(data?.states)) {
          for (const s of data.states) {
            const id = s.id ?? s.account_id ?? s.tg_account_id;
            if (id != null) out[id] = s.state ?? s;
          }
        } else if (data?.states && typeof data.states === "object") {
          for (const [id, st] of Object.entries(data.states)) {
            out[id] = st;
          }
        }
        return out;
      },

      // Conectar/iniciar bot
      start: async (id) => {
        const { data } = await req.post(
          `/telegram/accounts/${id}/start`,
          null
        );
        return data;
      },

      // Resetear sesión
      reset: async (id) => {
        const { data } = await req.post(
          `/telegram/accounts/${id}/reset`,
          null
        );
        return data;
      },

      createAccount: async (payload) => {
        const { data } = await req.post("/telegram/accounts", payload);
        return data;
      },

      deleteAccount: async (id) => {
        const { data } = await api.delete(`/telegram/accounts/${id}`);
        return data;
      },

      normalizeConnected,

      // ── OPCIONAL: MISMAS TABLAS DE CONEXIÓN QUE WA ────────────
      listConnections: async (channelId) => {
        if (!channelId) return [];
        const { data } = await req.get(`/channels/${channelId}/connections`);
        return data?.rows || data?.connections || [];
      },
      listBots: async () => {
        const { data } = await req.get(`/bots`);
        return data?.rows || data?.bots || [];
      },
      getConnectionBot: async (channelId, connId) => {
        const { data } = await req.get(
          `/channels/${channelId}/connections/${connId}/bot`
        );
        return { bot_id: data?.bot_id ?? null, is_active: !!data?.is_active };
      },
      setConnectionBot: async (channelId, connId, payload) => {
        const { data } = await req.post(
          `/channels/${channelId}/connections/${connId}/bot`,
          payload
        );
        return data;
      },
    };
  }, []);

  // Row para asignar bot a una conexión concreta (igual filosofía que WA)
  const ConnectionBotRow = ({ channelId, conn }) => {
    const key = `${channelId}:${conn.id}`;
    const cur = connBotMap[key] || { bot_id: null, is_active: 0 };
    const [saving, setSaving] = useState(false);

    const onSave = async (patch) => {
      setSaving(true);
      try {
        const payload = {
          bot_id: patch.bot_id ?? cur.bot_id,
          is_active: (patch.is_active ?? cur.is_active) ? 1 : 0,
        };
        await ep.setConnectionBot(channelId, conn.id, payload);
        setConnBotMap((m) => ({
          ...m,
          [key]: { bot_id: payload.bot_id, is_active: !!payload.is_active },
        }));
      } catch (e) {
        alert(
          e?.response?.data?.error ||
            "No se pudo guardar el bot de la conexión (Telegram)."
        );
      } finally {
        setSaving(false);
      }
    };

    return (
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr auto",
          gap: 8,
          alignItems: "center",
        }}
      >
        <div>
          <div style={{ fontWeight: 600 }}>
            {conn.name ||
              conn.label ||
              `${conn.provider_code || conn.provider}`}
          </div>
          <div style={{ fontSize: 12, opacity: 0.7 }}>
            {conn.provider_code || conn.provider} ·{" "}
            {conn.identifier ||
              conn.bot_username ||
              conn.chat_id ||
              conn.id}
          </div>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <select
            className="input"
            value={cur.bot_id || ""}
            onChange={(e) =>
              onSave({ bot_id: Number(e.target.value) || null })
            }
            disabled={saving}
          >
            <option value="">— Sin bot (usa bot del canal) —</option>
            {bots.map((b) => (
              <option key={b.id} value={b.id}>
                {b.name || `Bot #${b.id}`}
              </option>
            ))}
          </select>
          <label
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              fontSize: 12,
            }}
          >
            <input
              type="checkbox"
              checked={!!cur.is_active}
              onChange={(e) => onSave({ is_active: e.target.checked })}
              disabled={saving}
            />
            Activo
          </label>
        </div>
        <div>
          <button
            className="btn btn-muted"
            onClick={() => onSave({})}
            disabled={saving}
          >
            {saving ? "Guardando..." : "Guardar"}
          </button>
        </div>
      </div>
    );
  };

  // Cargar lista de cuentas Telegram
  const fetchAccounts = async () => {
    try {
      setError("");
      const list = await ep.listAccounts();
      setAccounts(list);

      // si quieres, puedes inicializar estados conectados con un snapshot
      try {
        const snap = await ep.getStatesSnapshot();
        const nextConn = {};
        const nextRaw = {};
        for (const acc of list) {
          const st = snap?.[acc.id];
          if (!st) continue;
          nextConn[acc.id] = ep.normalizeConnected(st);
          nextRaw[acc.id] = st;
        }
        if (Object.keys(nextConn).length) {
          setConnectedMap((m) => ({ ...m, ...nextConn }));
          setStateRawMap((m) => ({ ...m, ...nextRaw }));
        }
      } catch {}
    } catch (e) {
      console.error("fetchAccounts Telegram error:", e?.response?.data || e);
      setError(e?.response?.data?.error || "No se pudo cargar Telegram.");
      setAccounts([]);
    }
  };

  // Cargar bots una vez (para asignar por conexión)
  useEffect(() => {
    (async () => {
      try {
        const list = await ep.listBots();
        setBots(list);
      } catch {}
    })();
  }, [ep]);

  // Cargar conexiones por canal (si tg_accounts tiene channel_id)
  useEffect(() => {
    (async () => {
      const next = {};
      const nextConnBot = {};
      for (const acc of accounts) {
        const channelId = acc.channel_id || acc.channelId;
        if (!channelId) continue;
        try {
          const conns = await ep.listConnections(channelId);
          next[channelId] = conns;
          for (const c of conns) {
            try {
              const st = await ep.getConnectionBot(channelId, c.id);
              nextConnBot[`${channelId}:${c.id}`] = st;
            } catch {}
          }
        } catch {}
      }
      setConnectionsMap(next);
      setConnBotMap((m) => ({ ...m, ...nextConnBot }));
    })();
  }, [accounts, ep]);

  useEffect(() => {
    fetchAccounts();
  }, []);

  // Poll de estados cada 3s (por cuenta; si prefieres, puedes usar snapshot)
  useEffect(() => {
    if (pollRef.current.status) clearInterval(pollRef.current.status);
    pollRef.current.status = setInterval(async () => {
      if (!accounts.length) return;
      for (const acc of accounts) {
        try {
          const st = await ep.getState(acc.id);
          setStateRawMap((m) => ({ ...m, [acc.id]: st }));
          const isConn = ep.normalizeConnected(st);
          setConnectedMap((m) => ({ ...m, [acc.id]: !!isConn }));
        } catch {}
      }
    }, 3000);
    return () => {
      if (pollRef.current.status) clearInterval(pollRef.current.status);
      pollRef.current.status = null;
    };
  }, [accounts, ep]);

  // Acciones UI
  const handleStart = async (acc) => {
    try {
      await ep.start(acc.id);
      // el poll se encargará de refrescar estados
      alert(`Bot de Telegram iniciado para "${acc.name || acc.username}".`);
    } catch (e) {
      alert(
        e?.response?.data?.error ||
          "No se pudo iniciar el bot de Telegram."
      );
    }
  };

  const handleReset = async (acc) => {
    if (
      !window.confirm(
        `Esto reiniciará el bot de "${acc.name || acc.username}". ¿Continuar?`
      )
    )
      return;
    try {
      await ep.reset(acc.id);
      alert(
        `Sesión de Telegram reseteada para "${acc.name || acc.username}".`
      );
    } catch (e) {
      alert(
        e?.response?.data?.error ||
          "No se pudo resetear la sesión de Telegram."
      );
    }
  };

  const handleCreateAccount = async (e) => {
    e.preventDefault();
    try {
      setSavingAccount(true);
      setError("");
      await ep.createAccount({
        name: form.name.trim() || null,
        username: form.username.trim().replace(/^@/, "") || null,
        token: form.token.trim(),
      });
      setForm({ name: "", username: "", token: "" });
      await fetchAccounts();
    } catch (err) {
      setError(err?.response?.data?.error || "No se pudo guardar el bot de Telegram.");
    } finally {
      setSavingAccount(false);
    }
  };

  const handleDeleteAccount = async (acc) => {
    if (!window.confirm(`Eliminar "${acc.name || acc.username || `Bot #${acc.id}`}"?`)) {
      return;
    }
    try {
      setError("");
      await ep.deleteAccount(acc.id);
      await fetchAccounts();
    } catch (err) {
      setError(err?.response?.data?.error || "No se pudo eliminar la cuenta.");
    }
  };

  return (
    <>
      <div className="page-title">Telegram</div>

      <section className="card section" style={{ maxWidth: 980 }}>
        <h3 style={{ marginTop: 0 }}>Bots / Cuentas de Telegram</h3>
        <p className="text-sm text-[color:var(--muted)]">
          Desde aquí gestionas múltiples bots de Telegram (una cuenta por token).
          Las cuentas se almacenan en <code>tg_accounts</code> y comparten el
          mismo modelo de leads, threads y mensajes que WhatsApp.
        </p>

        {error && (
          <div className="text-sm text-red-300 border border-red-500/40 rounded p-2 mb-3">
            {error}
          </div>
        )}

        <form
          onSubmit={handleCreateAccount}
          className="card"
          style={{ padding: 12, marginBottom: 16 }}
        >
          <div className="font-bold mb-2 text-sm">Nuevo bot Telegram</div>
          <div
            className="grid gap-2"
            style={{ gridTemplateColumns: "1fr 1fr 2fr auto", alignItems: "end" }}
          >
            <div>
              <label className="text-xs opacity-80">Nombre</label>
              <input
                className="input"
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                placeholder="Atención Telegram"
              />
            </div>
            <div>
              <label className="text-xs opacity-80">Usuario</label>
              <input
                className="input"
                value={form.username}
                onChange={(e) => setForm((f) => ({ ...f, username: e.target.value }))}
                placeholder="@mi_bot"
              />
            </div>
            <div>
              <label className="text-xs opacity-80">Token de BotFather *</label>
              <input
                className="input"
                value={form.token}
                onChange={(e) => setForm((f) => ({ ...f, token: e.target.value }))}
                placeholder="123456:ABC..."
                required
              />
            </div>
            <button className="btn" disabled={savingAccount || !form.token.trim()}>
              {savingAccount ? "Guardando..." : "Guardar bot"}
            </button>
          </div>
          <div className="text-xs opacity-70 mt-2">
            Crea el bot con BotFather, pega el token aquí y luego pulsa Iniciar bot.
          </div>
        </form>

        {accounts.length === 0 && (
          <div className="muted">
            No hay bots de Telegram registrados en la base de datos.
          </div>
        )}

        {/* Leyenda estado */}
        <div className="flex gap-3 items-center my-2">
          <span className="inline-flex items-center gap-1 text-xs opacity-80">
            <span
              style={{
                width: 10,
                height: 10,
                borderRadius: "50%",
                background: "#22c55e",
                border: "2px solid #0f172a",
              }}
            />
            Conectado / en ejecución
          </span>
          <span className="inline-flex items-center gap-1 text-xs opacity-80">
            <span
              style={{
                width: 10,
                height: 10,
                borderRadius: "50%",
                background: "#ef4444",
                border: "2px solid #0f172a",
              }}
            />
            Detenido / desconectado
          </span>
        </div>

        {/* GRID de cuentas TG */}
        <div
          className="grid gap-4"
          style={{
            gridTemplateColumns:
              "repeat(auto-fill, minmax(360px, 1fr))",
          }}
        >
          {accounts.map((acc) => {
            const connected = Boolean(connectedMap[acc.id]);
            const dotColor = connected ? "#22c55e" : "#ef4444";
            const channelId = acc.channel_id ?? acc.channelId; // opcional
            const channelConns = channelId
              ? connectionsMap[channelId] || []
              : [];
            const raw = stateRawMap[acc.id];

            return (
              <div key={acc.id} className="card" style={{ padding: 16 }}>
                {/* Cabecera */}
                <div
                  className="grid"
                  style={{
                    gridTemplateColumns: "1fr auto",
                    gap: 12,
                    alignItems: "center",
                  }}
                >
                  <div>
                    <div className="flex items-center gap-2">
                      <span
                        title={
                          connected
                            ? "Bot conectado"
                            : "Bot desconectado"
                        }
                        style={{
                          width: 12,
                          height: 12,
                          borderRadius: "50%",
                          background: dotColor,
                          border: "2px solid #0f172a",
                          flex: "0 0 auto",
                        }}
                      />
                      <div className="font-bold text-base">
                        {acc.name || acc.username || `Bot #${acc.id}`}
                      </div>
                    </div>
                    <div className="text-xs opacity-80 flex flex-wrap gap-2 mt-1">
                      {acc.username && (
                        <>
                          <span>@{acc.username}</span>
                          <span>·</span>
                        </>
                      )}
                      <span>
                        {connected
                          ? "Conectado / recibiendo updates"
                          : "Detenido / sin updates"}
                      </span>
                      {acc.created_at && (
                        <>
                          <span>·</span>
                          <span>
                            Creado:{" "}
                            {new Date(acc.created_at).toLocaleString()}
                          </span>
                        </>
                      )}
                    </div>
                  </div>

                  <div className="flex gap-2 flex-wrap justify-end">
                    <button
                      className="btn"
                      onClick={() => handleStart(acc)}
                    >
                      {connected ? "Reiniciar bot" : "Iniciar bot"}
                    </button>
                    <button
                      className="btn btn-warn"
                      onClick={() => handleReset(acc)}
                    >
                      Reset sesión
                    </button>
                    <button
                      className="btn btn-danger"
                      onClick={() => handleDeleteAccount(acc)}
                    >
                      Eliminar
                    </button>
                  </div>
                </div>

                {/* Conexiones del canal (si integras con channels/channel_connections) */}
                {channelId && channelConns.length > 0 && (
                  <div className="mt-3">
                    <div
                      className="card"
                      style={{ padding: 12, marginTop: 4 }}
                    >
                      <div className="font-bold mb-2 text-sm">
                        Conexiones del canal (Telegram)
                      </div>
                      <div className="grid gap-2">
                        {channelConns.map((conn) => (
                          <ConnectionBotRow
                            key={conn.id}
                            channelId={channelId}
                            conn={conn}
                          />
                        ))}
                      </div>
                      <div className="text-xs opacity-70 mt-2">
                        Si una conexión no tiene bot activo, se usará el bot
                        configurado a nivel de <b>canal</b> como fallback.
                      </div>
                    </div>
                  </div>
                )}

                {/* Estado crudo (debug) */}
                <details
                  className="mt-3"
                  style={{ fontSize: 12, opacity: 0.9 }}
                >
                  <summary>Ver estado crudo</summary>
                  <pre
                    style={{
                      marginTop: 6,
                      maxHeight: 200,
                      overflow: "auto",
                      background: "#020617",
                      color: "#e5e7eb",
                      padding: 8,
                      borderRadius: 6,
                      fontSize: 11,
                    }}
                  >
                    {JSON.stringify(raw ?? {}, null, 2)}
                  </pre>
                </details>
              </div>
            );
          })}
        </div>
      </section>
    </>
  );
}
